"use client";

import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem } from "./Breadcrumb";

interface NavigationHeaderProps {
  items: BreadcrumbItem[];
  className?: string;
  onBackClick?: () => void;
}

export const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  items,
  className = "",
  onBackClick,
}) => {
  const navigate = useNavigate();

  // If only 2 items (root + current), show back button instead of breadcrumbs
  const shouldShowBackButton = items.length === 2;
  const shouldShowBreadcrumbs = items.length > 2;

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else if (items.length > 1) {
      const previousItem = items[items.length - 2];
      if (previousItem.href) {
        navigate(previousItem.href);
      } else if (previousItem.onClick) {
        previousItem.onClick();
      } else {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  if (shouldShowBackButton) {
    return (
      <div className={`mb-4 ${className}`}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleBackClick}
            className="flex items-center px-3 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/80 transition-colors"
          >
            <ChevronLeft size={18} className="mr-1" />
            Back to {items[0]?.label || "Previous"}
          </button>
          <span className="text-muted-foreground">
            {items[items.length - 1]?.label}
          </span>
        </div>
      </div>
    );
  }

  if (shouldShowBreadcrumbs) {
    return (
      <div className={`mb-4 ${className}`}>
        <Breadcrumb items={items} showBackButton={false} />
      </div>
    );
  }

  // Fallback: single item or empty, just show the current page title
  return (
    <div className={`mb-4 ${className}`}>
      <h1 className="text-xl font-semibold">
        {items[items.length - 1]?.label || "Page"}
      </h1>
    </div>
  );
};
